/**
 * RMA write endpoints for odl_app.
 *
 * The return-materials-authorization work queue is browsed via EntityGrid
 * (dataSource/apiClient) — no list endpoint lives here. This module injects the
 * odl-app RMA write lifecycle into entitiesApi and re-exports the generated
 * hooks the returns UI drives: issue (create), edit (patch), process & ship,
 * quick-ship out, invoice, and return-photo removal. (The BE viewset also
 * exposes retrieve, undoprocess, and receivequickship; hooks for those are
 * added when a consumer exists — the detail read in particular awaits a fuller
 * RMADetailSerializer than the current grid-detail shape.)
 *
 * All endpoints live under /api/odl-app/rmas/ and ride the shared axiosBaseQuery
 * (plain `query`). Every RMA-returning endpoint wraps the body in the odl-app
 * { "data": … } envelope, so transformResponse is the uniform `b => b.data`
 * (never `?? b` — there are no bare RPC results; the actions echo the full RMA).
 * Picture-carrying writes pass a FormData as `data`; axios sets the multipart
 * boundary itself (the apiClient pins no default Content-Type — axiosInit only
 * sets Accept), so no queryFn escape hatch is needed and the uniform
 * transformResponse still applies.
 *
 * Tags invalidate the Rma LIST (+ the touched id) so any RTKQ read consumer
 * refetches. The EntityGrid list rides the MUI dataSource (apiClient), which tag
 * invalidation does NOT reach — grid callers refresh the grid themselves after a
 * mutation resolves.
 *
 * Contains:
 * - rmasApi       — the injected endpoint slice
 * - rmaWriteBody  — JSON-or-multipart body builder (picture-aware)
 * - hook re-exports (see the named export block)
 */

import { entitiesApi } from './entitiesApi';

/**
 * Build the axiosBaseQuery `data` for an RMA write.
 *
 * With a `picture` blob the body must ride as multipart/form-data — the legacy
 * RMA write path pops `picture` and persists it as a BinaryFile. axios sets the
 * multipart boundary itself once `data` is a FormData (the apiClient pins no
 * default Content-Type), so this stays on the plain `query` path and keeps the
 * uniform transformResponse. With no blob the plain object goes out as JSON.
 *
 * `*_ids` array fields stringify to the CSV form the backend `_coerce_csv_ids`
 * expects; nested objects are NOT multipart-safe — shape them before passing.
 *
 * @param {object} [body]       serializer input fields
 * @param {Blob|File} [picture] optional return-photo blob
 * @returns {FormData|object}
 */
function rmaWriteBody(body = {}, picture) {
  if (!picture) return body;
  const form = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });
  form.append('picture', picture);
  return form;
}

const rmasApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Issue a new RMA (RMAInitialDetailsSerializer input). Returns detail (201).
     *
     * @param {{ body: object, picture?: Blob|File }} arg
     */
    createRma: build.mutation({
      query: ({ body, picture }) => ({
        url: 'rmas/',
        method: 'post',
        data: rmaWriteBody(body, picture),
      }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'Rma', id: 'LIST' }],
    }),

    /**
     * Partial-update an RMA (RMASerializer input; optional return photo).
     *
     * @param {{ id: string|number, body: object, picture?: Blob|File }} arg
     */
    patchRma: build.mutation({
      query: ({ id, body, picture }) => ({
        url: `rmas/${id}/`,
        method: 'patch',
        data: rmaWriteBody(body, picture),
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Rma', id: 'LIST' },
        { type: 'Rma', id },
      ],
    }),

    /**
     * Process an incoming RMA and ship the replacement (status → 1).
     *
     * @param {{ id: string|number, body?: object, picture?: Blob|File }} arg
     */
    processRma: build.mutation({
      query: ({ id, body, picture }) => ({
        url: `rmas/${id}/process/`,
        method: 'post',
        data: rmaWriteBody(body, picture),
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Rma', id: 'LIST' },
        { type: 'Rma', id },
      ],
    }),

    /**
     * Quick-ship a replacement out to the customer (status → 2).
     * 400 → error.data.detail for a non-quick-ship RMA.
     *
     * @param {{ id: string|number, body?: object, picture?: Blob|File }} arg
     */
    sendQuickShipRma: build.mutation({
      query: ({ id, body, picture }) => ({
        url: `rmas/${id}/sendquickship/`,
        method: 'post',
        data: rmaWriteBody(body, picture),
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Rma', id: 'LIST' },
        { type: 'Rma', id },
      ],
    }),

    /**
     * Invoice a shipped RMA and mark it complete (status → 100).
     *
     * @param {{ id: string|number, body?: object, picture?: Blob|File }} arg
     */
    invoiceRma: build.mutation({
      query: ({ id, body, picture }) => ({
        url: `rmas/${id}/invoice/`,
        method: 'post',
        data: rmaWriteBody(body, picture),
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Rma', id: 'LIST' },
        { type: 'Rma', id },
      ],
    }),

    /**
     * Remove the return photo (legacy ImageField). Returns the refreshed RMA.
     *
     * @param {{ id: string|number }} arg
     */
    deleteRmaImage: build.mutation({
      query: ({ id }) => ({ url: `rmas/${id}/deleteimage/`, method: 'delete' }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Rma', id: 'LIST' },
        { type: 'Rma', id },
      ],
    }),
  }),
});

export { rmasApi };
export const {
  useCreateRmaMutation,
  usePatchRmaMutation,
  useProcessRmaMutation,
  useSendQuickShipRmaMutation,
  useInvoiceRmaMutation,
  useDeleteRmaImageMutation,
} = rmasApi;
